import { EnumVideoProcessor } from '@/app/Sites/siteApi';
import { MonitorAlertEventDto } from "@/app/Alert/types";

export type VideoProcessor = {
  id: string;
  identifier?: string;
  name: string;
  events: MonitorAlertEventDto[];
}

/** Extracts a list of (sorted) processors from a list of events */
export const extractVideoProcessors = (events: MonitorAlertEventDto[]) => {
  // Group events by video processor
  const videoProcessors = events.reduce<VideoProcessor[]>((acc, event) => {
    if (!event.videoProcessorId) return acc;

    const existing = acc.find((p) => p.id === event.videoProcessorId);
    if (existing) {
      existing.events.push(event);
      return acc;
    }

    acc.push({
      id: event.videoProcessorId,
      identifier: event.videoProcessorIdentifier,
      name:
        event.videoProcessorDescription ||
        event.nvrDeviceName ||
        event.videoProcessorId ||
        'Unknown Camera',
      events: [event],
    });

    return acc;
  }, []);

  // Sort events by occurrence time for each processor
  const sortedProcessors = videoProcessors.map((processor) => ({
    ...processor,
    events: [...processor.events].sort(
      (a, b) => new Date(a.occurred || 0).getTime() - new Date(b.occurred || 0).getTime()
    ),
  }));

  return sortedProcessors
}

export const videoProcessorDisplayNames: Record<EnumVideoProcessor, string> = {
  UmboCv: 'Umbo CV',
  ThreeDEye: '3dEye',
  Smtp: 'SMTP',
  Milestone: 'Milestone',
  Camect: 'Camect',
  NxWitness: 'NxWitness',
  HikVisionNvr: 'HikVision NVR',
  DahuaCamera: 'Dahua',
  OpenEye: 'OpenEye',
};

export function getVideoProcessorDisplayName(event: Pick<MonitorAlertEventDto, 'videoProcessorIdentifier' | 'videoProcessorDescription' | 'nvrDeviceName' | "videoProcessor" | "videoProcessorId">): string {
  return event.nvrDeviceName
    ?? event.videoProcessorDescription
    ?? event.videoProcessorId
    ?? videoProcessorDisplayNames[event.videoProcessor as EnumVideoProcessor]
    ?? "Unknown";
}

interface CameraFeedEvent extends Partial<Pick<MonitorAlertEventDto, 'videoProcessorIdentifier' | 'videoProcessorDescription' | 'nvrDeviceName' | 'videoProcessor' | 'videoProcessorId'>> {
  assetName?: string;
  assetDto?: { name?: string };
}

export function getCameraFeedName(events?: CameraFeedEvent[]): string | undefined {
  if (!events?.length) return undefined;
  const getAssetName = (e: CameraFeedEvent) => e.assetName || e.assetDto?.name;
  const name = getAssetName(events[0]) || events.map(getAssetName).find(Boolean);
  if (name) return name;
  const displayName = getVideoProcessorDisplayName(events[0]);
  if (displayName && displayName !== 'Unknown') return displayName;
  return undefined;
}